/**
 * Payabli's check scanner only takes photos from a camera, so a laptop with no
 * camera — or one pointed at the operator — cannot scan a check at all.
 *
 * The camera is not inside Payabli's iframe. When staff press Front or Back in
 * the iframe, it posts `open-modal-to-capture-check` to the page, and the
 * component object on the page answers by calling its own
 * `this.captureImageInModal({ html, side })`: that opens the camera, and
 * resolves `{ data: <1280×640 JPEG data URL>, side }`, which the component
 * posts back into the iframe (`check-image-captured`) to be read. Closing the
 * camera with Escape REJECTS with `Error('User cancelled the capture')`, and
 * the component posts nothing back — the iframe keeps waiting for a photo.
 *
 * `interceptCheckCapture` replaces that method on ONE instance with a chooser
 * of ours, which answers with the same `{ data, side }` from a photo on disk,
 * or hands the very same call to the camera. A component without the method
 * (a bundle that moved the camera) is left alone: camera-only, as before.
 */

/** What the iframe asks the component for: the modal's HTML and the side. */
export type CheckCaptureArgs = [data?: { html?: string; side?: unknown }, ...rest: unknown[]];

/** What the component posts back into the iframe. */
export type CheckCaptureResult = { data: string; side: unknown };

type CaptureMethod = (...args: CheckCaptureArgs) => Promise<CheckCaptureResult>;

/** One press of Front or Back, waiting for a photo. */
export interface CheckCaptureRequest {
  /** As the iframe named it: `'front'` for the front, anything else the back. */
  side: unknown;
  /** The component's own camera, called exactly as the iframe called it. */
  openCamera: () => Promise<CheckCaptureResult>;
}

/** The error the camera rejects with when it is closed without a photo. */
export const CHECK_CAPTURE_CANCELLED_MESSAGE = 'User cancelled the capture';

export function checkCaptureCancelled(): Error {
  return new Error(CHECK_CAPTURE_CANCELLED_MESSAGE);
}

/** What the chooser tells staff about taking the photo. */
export const CHECK_PHOTO_HINT =
  'Photograph the check flat on a dark surface, all four corners in view.';

/** True when this browser can open a camera at all. */
export function canUseCamera(): boolean {
  return (
    typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

/** True when the side is the front, the way the component's camera decides it. */
export function isFrontSide(side: unknown): boolean {
  return side === 'front';
}

/**
 * Route `instance`'s check captures through `choose`. Returns the function
 * that puts the component's own method back, or null when there was nothing
 * to wrap. Should `choose` itself throw, the camera opens as it always did.
 */
export function interceptCheckCapture(
  instance: object,
  choose: (request: CheckCaptureRequest) => Promise<CheckCaptureResult>,
): (() => void) | null {
  const target = instance as { captureImageInModal?: unknown };
  const original = target.captureImageInModal;
  if (typeof original !== 'function') return null;
  const hadOwn = Object.prototype.hasOwnProperty.call(target, 'captureImageInModal');
  const camera = original as CaptureMethod;

  const wrapped: CaptureMethod = (...args) => {
    const openCamera = () => camera.apply(instance, args);
    try {
      return choose({ side: args[0]?.side, openCamera });
    } catch {
      return openCamera();
    }
  };
  target.captureImageInModal = wrapped;

  return () => {
    // Only undo our own wrapper: something that replaced it since is not ours.
    if (target.captureImageInModal !== wrapped) return;
    if (hadOwn) target.captureImageInModal = original;
    else delete target.captureImageInModal;
  };
}
